import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * Two behaviours, switched by the `pagination-mode` attribute on the host element:
 * - attribute absent (or any value other than `load_more`): the stock behaviour. The next and previous pages are
 *   prefetched and appended by an IntersectionObserver (infinite scroll) when the `viewMore*` sentinels exist.
 *   Search results and the blog list still run this way.
 * - `pagination-mode="load_more"` (SJ collection pages): nothing is prefetched and nothing is observed. The next
 *   page is requested only when the visitor activates the Load more control, see `loadMore`.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLAnchorElement} [loadMoreButton] - The Load more control (load-more mode only).
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Load-more mode: true while a batch is being fetched, so a double tap cannot request it twice.
   * @type {boolean}
   */
  #loadMoreBusy = false;

  /**
   * Load-more mode: bumped by every request and by every filter/sort update. A response whose id is no longer the
   * current one belongs to a grid that has since been re-rendered, and is dropped.
   * @type {number}
   */
  #loadMoreRequestId = 0;

  /**
   * Whether this list only loads further pages on demand.
   * @returns {boolean}
   */
  get isLoadMoreMode() {
    return this.getAttribute('pagination-mode') === 'load_more';
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Load-more mode never fetches ahead of the click and never observes the scroll position.
    if (!this.isLoadMoreMode) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Load-more mode: fetches ONLY the next page, on demand, and appends its cards to the grid.
   *
   * Bound declaratively in snippets/product-grid.liquid (`on:click="/loadMore"`). The control is a real link to the
   * next page, so without JavaScript - or if this request fails - the visitor still reaches those products.
   *
   * @param {MouseEvent} event - The click event (proxied by component.js when an inner element was clicked).
   */
  async loadMore(event) {
    if (!this.isLoadMoreMode) return;

    // Leave modified clicks (open in a new tab / window) to the browser.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;

    const button = this.#getLoadMoreButton();
    if (!button) return;

    // Must run before the first await, otherwise the browser has already followed the link.
    event.preventDefault();

    if (this.#loadMoreBusy) return;

    // A click dispatched by the keyboard (Enter on the link) carries detail 0; pointer clicks carry 1 or more.
    const activatedByKeyboard = event.detail === 0;

    /** @type {HTMLElement | null} */
    const grid = this.querySelector('[ref="grid"]');
    const nextPage = grid ? this.#getNextLoadMorePage(grid) : undefined;

    if (!grid || !nextPage) {
      // The next page cannot be worked out from the DOM: behave like the plain link.
      window.location.assign(button.href);
      return;
    }

    const knownLastPage = Number(grid.dataset.lastPage);
    if (knownLastPage && nextPage.page > knownLastPage) {
      this.#finishLoadMore(button);
      return;
    }

    this.#loadMoreBusy = true;
    const requestId = ++this.#loadMoreRequestId;
    this.#setLoadMoreBusy(button, true);

    try {
      // section-renderer adds `section_id` to the URL object it is given, so hand it a copy and keep ours clean.
      const pageHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(nextPage.url.href));

      // A filter or sort update re-rendered the grid while this batch was loading: it no longer belongs here.
      if (requestId !== this.#loadMoreRequestId || !this.isConnected) return;

      const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
      const parsedGrid = parsedPage.querySelector('[ref="grid"]');
      const parsedCards = parsedGrid ? Array.from(parsedGrid.querySelectorAll(':scope > [ref="cards[]"]')) : [];

      if (parsedCards.length === 0) {
        throw new Error(`Load more: page ${nextPage.page} came back without products`);
      }

      // The collection can change between two requests; never add a product that is already in the grid.
      const existingIds = new Set(Array.from(grid.children, (card) => card.id).filter(Boolean));
      const newCards = parsedCards.filter((card) => !card.id || !existingIds.has(card.id));

      if (newCards.length === 0) {
        throw new Error(`Load more: page ${nextPage.page} only repeated products that are already listed`);
      }

      // Everything appended here sits below the fold, whatever the server picked for a first-row image.
      for (const card of newCards) {
        for (const image of card.querySelectorAll('img')) {
          image.setAttribute('loading', 'lazy');
          image.setAttribute('decoding', 'async');
          image.removeAttribute('fetchpriority');
        }
      }

      grid.append(...newCards);

      this.#aspectRatioHelper?.processNewElements();

      // replaceState, not pushState: Back should leave the collection, not step back through the batches.
      history.replaceState(history.state, '', nextPage.url.toString());

      const parsedLastPage = parsedGrid instanceof HTMLElement ? parsedGrid.dataset.lastPage : undefined;
      if (parsedLastPage && parsedLastPage !== grid.dataset.lastPage) {
        grid.dataset.lastPage = parsedLastPage;
      }

      const lastPage = Number(grid.dataset.lastPage);
      const hasMore = Boolean(lastPage) && nextPage.page < lastPage;
      const shown = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
      const parsedTotal = parsedPage.querySelector('[data-sj-loadmore-total]')?.textContent?.trim();
      const total = this.#updateLoadMoreCount(shown, parsedTotal);

      if (hasMore) {
        const followingUrl = new URL(nextPage.url.href);
        followingUrl.searchParams.set('page', (nextPage.page + 1).toString());
        button.setAttribute('href', `${followingUrl.pathname}${followingUrl.search}`);
        this.#setLoadMoreBusy(button, false);
      }

      this.#announceLoadMore(button, { added: newCards.length, shown, total, hasMore });

      // Keyboard users carry on from the first product that was just added instead of tabbing back up to it.
      // Pointer users keep their focus where it is: on desktop, focusing a card makes product-card.js prefetch
      // that product page for quick add, which nobody asked for yet.
      if (activatedByKeyboard) {
        const firstNewLink = newCards[0]?.querySelector('a[href]');
        if (firstNewLink instanceof HTMLElement) firstNewLink.focus();
      }

      if (!hasMore) this.#finishLoadMore(button);
    } catch (error) {
      console.error(error);

      if (requestId === this.#loadMoreRequestId) {
        // Restore the control, then fall back to a normal page load of the next page.
        this.#setLoadMoreBusy(button, false);
        window.location.assign(nextPage.url.href);
      }
    } finally {
      if (requestId === this.#loadMoreRequestId) this.#loadMoreBusy = false;
    }
  }

  /**
   * Load-more mode: the control as it is in the DOM right now. A filter or sort update morphs the section, so the
   * element is looked up on every use instead of being held on to.
   * @returns {HTMLAnchorElement | null}
   */
  #getLoadMoreButton() {
    const button = this.querySelector('[ref="loadMoreButton"]');

    return button instanceof HTMLAnchorElement ? button : null;
  }

  /**
   * Load-more mode: works out the next page from the last card that is actually in the grid.
   * @param {HTMLElement} grid - The grid element.
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextLoadMorePage(grid) {
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastCard = cards[cards.length - 1];

    if (!(lastCard instanceof HTMLElement)) return;

    const currentPage = Number(lastCard.dataset.page);
    if (!Number.isInteger(currentPage) || currentPage < 1) return;

    const page = currentPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  /**
   * Load-more mode: toggles the loading state of the control.
   * @param {HTMLAnchorElement} button - The Load more control.
   * @param {boolean} busy - Whether a batch is being fetched.
   */
  #setLoadMoreBusy(button, busy) {
    const label = button.querySelector('[data-sj-loadmore-label]');

    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = button.dataset.loadingLabel || 'Loading…';
      return;
    }

    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
    if (label) label.textContent = button.dataset.label || 'Load more';
  }

  /**
   * Load-more mode: the last page is in the grid, so the control has nothing left to do.
   * @param {HTMLAnchorElement} button - The Load more control.
   */
  #finishLoadMore(button) {
    this.#setLoadMoreBusy(button, false);
    button.hidden = true;
  }

  /**
   * Load-more mode: refreshes the "Showing X of Y" line.
   * @param {number} shown - How many products are in the grid.
   * @param {string} [parsedTotal] - The total reported by the page that was just fetched.
   * @returns {string} The total that is now displayed.
   */
  #updateLoadMoreCount(shown, parsedTotal) {
    const shownElement = this.querySelector('[data-sj-loadmore-shown]');
    const totalElement = this.querySelector('[data-sj-loadmore-total]');

    if (shownElement) shownElement.textContent = shown.toString();
    if (totalElement && parsedTotal) totalElement.textContent = parsedTotal;

    return totalElement?.textContent?.trim() || parsedTotal || shown.toString();
  }

  /**
   * Load-more mode: tells screen reader users what just happened, through the polite live region.
   * The sentences live in the markup (data attributes on the control) so the copy is edited in Liquid.
   * @param {HTMLAnchorElement} button - The Load more control.
   * @param {{ added: number, shown: number, total: string, hasMore: boolean }} result - What was loaded.
   */
  #announceLoadMore(button, { added, shown, total, hasMore }) {
    const status = this.querySelector('[data-sj-loadmore-status]');
    if (!status) return;

    const loaded = (button.dataset.loadedText || '[added] more products loaded. Showing [shown] of [total].')
      .replace('[added]', added.toString())
      .replace('[shown]', shown.toString())
      .replace('[total]', total);

    // "Showing X of Y" stays truthful for a visitor who started on ?page=N, where X never reaches Y.
    status.textContent = hasMore ? loaded : `${loaded} ${button.dataset.endText || 'End of the list.'}`;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    if (this.isLoadMoreMode) {
      // facets.js re-renders this section right after the event and morph.js swaps in the server's fresh Load more
      // control (href, label, count), so there is no state to repair here. All that is left is to drop a batch
      // that may still be in flight for the old result set - and, unlike the stock path below, NOT to prefetch.
      this.#loadMoreRequestId++;
      this.#loadMoreBusy = false;
      return;
    }

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
